import { useState } from "react";
import { BarChart3, Plus, X, Send } from "lucide-react";
import { toast } from "sonner";

export const CreatePollForm = ({ onSubmit, groupId = null, onCancel }) => {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [duration, setDuration] = useState(24);
  const [loading, setLoading] = useState(false);

  const addOption = () => {
    if (options.length < 6) setOptions([...options, ""]);
  };

  const removeOption = (idx) => {
    if (options.length > 2) setOptions(options.filter((_, i) => i !== idx));
  };

  const updateOption = (idx, val) => {
    setOptions(options.map((o, i) => (i === idx ? val : o)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validOptions = options.filter((o) => o.trim());
    if (!question.trim() || validOptions.length < 2) return;
    setLoading(true);
    try {
      await onSubmit({
        question: question.trim(),
        options: validOptions.map((text) => ({ text })),
        group_id: groupId,
        duration_hours: duration,
      });
      setQuestion("");
      setOptions(["", ""]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setLoading(false);
  };

  const validCount = options.filter((o) => o.trim()).length;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-card border border-border rounded-3xl p-5 space-y-4"
      data-testid="create-poll-form"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#7000FF]" />
          <h3 className="font-heading font-bold text-sm text-card-foreground">Creer un sondage</h3>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-card-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <input
        placeholder="Pose ta question..."
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none"
        data-testid="poll-question-input"
        maxLength={200}
      />

      <div className="space-y-2">
        {options.map((opt, idx) => (
          <div key={`opt-${idx}`} className="flex gap-2">
            <input
              placeholder={`Option ${idx + 1}`}
              value={opt}
              onChange={(e) => updateOption(idx, e.target.value)}
              className="flex-1 bg-white border border-border rounded-xl px-4 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#7000FF] focus:ring-1 focus:ring-[#7000FF] outline-none"
              data-testid={`poll-option-input-${idx}`}
              maxLength={100}
            />
            {options.length > 2 && (
              <button
                type="button"
                onClick={() => removeOption(idx)}
                className="p-2 text-muted-foreground hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {options.length < 6 && (
          <button
            type="button"
            onClick={addOption}
            className="flex items-center gap-1.5 text-xs text-[#7000FF] hover:text-[#5500CC] transition-colors font-medium"
            data-testid="poll-add-option-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Ajouter une option
          </button>
        )}
      </div>

      {/* Duration selector */}
      <div>
        <p className="text-xs text-muted-foreground mb-2">Duree du sondage</p>
        <div className="flex gap-1.5">
          {[6, 12, 24, 48, 72].map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setDuration(h)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                duration === h
                  ? "bg-[#7000FF]/10 border-[#7000FF]/40 text-[#7000FF] font-medium"
                  : "bg-white border-border text-muted-foreground hover:border-[#7000FF]/20"
              }`}
              data-testid={`poll-duration-${h}`}
            >
              {h < 24 ? `${h}h` : `${h / 24}j`}
            </button>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading || !question.trim() || validCount < 2}
        className="bg-[#7000FF] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform duration-200 disabled:opacity-50 flex items-center gap-2"
        data-testid="poll-submit-btn"
      >
        <Send className="w-4 h-4" />
        {loading ? "..." : "Lancer le sondage"}
      </button>
    </form>
  );
};
